import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  ActivityIndicator,
  StyleSheet,
} from 'react-native';
import type { LaunchSecureDto, PatinfoProfileDto, PatinfoPutRequestDto, PatinfoSearchResultDto } from '../../../types/api/patinfo';
import { normalizeHistoryAdminResponse } from '../../../types/api/patinfo';
import type { AccountPlaDto, AccountSraDto, AdminHtmlAnswerDto, MVarDto } from '../../../types/api';
import type { ClientTouchpointSchedule } from '../../../services/clients/ClientFormBuilder';
import type { AdminAssessmentDto } from '../../../types/adminAssessment';
import type { ClientEnableFlags } from './ClientEnableFlags';
import { ProfileRowEditor, type ViewReportParams } from './ProfileRowEditor';
import { loadAssessmentsBySourceId, resolveHistoryItemReport, resolveDefaultReportId, getCachedAssessmentsBySourceId, ASSESSMENT_CONTENT_UPDATED_EVENT } from '../../../services/clients/ClientReportAssessments';
import { EDITOR_COLORS, editorText } from '../../../styles/editorTheme';

interface RecentReportsResultsListProps {
  results: PatinfoSearchResultDto[];
  profiles: PatinfoProfileDto[];
  isLoading: boolean;
  error: string | null;
  blocknum: string;
  blocksize: string;
  hasMore: boolean;
  enableFlags: ClientEnableFlags;
  onNextPage: () => void;
  onPrevPage: () => void;
  onSaveProfile: (payload: PatinfoPutRequestDto) => Promise<void>;
  onSendNow: (profile: PatinfoProfileDto, request: LaunchSecureDto) => Promise<void>;
  onCancelLaunch: (profile: PatinfoProfileDto, request: LaunchSecureDto) => Promise<void>;
  onViewReport?: (params: ViewReportParams) => void;
  onRefresh: () => void;
  sendAssessmentOptions?: AdminHtmlAnswerDto[];
  officeOnlineSraMembers?: AccountSraDto[];
  assessmentOptions?: AccountPlaDto[];
  clientPhiFields?: MVarDto[];
  genderPhiOptions?: AdminHtmlAnswerDto[];
  touchpointSchedules?: ClientTouchpointSchedule[];
}

export const RecentReportsResultsList: React.FC<RecentReportsResultsListProps> = ({
  results,
  profiles,
  isLoading,
  error,
  blocknum,
  blocksize,
  hasMore,
  enableFlags,
  onNextPage,
  onPrevPage,
  onSaveProfile,
  onSendNow,
  onCancelLaunch,
  onViewReport,
  onRefresh,
  sendAssessmentOptions = [],
  officeOnlineSraMembers = [],
  assessmentOptions = [],
  clientPhiFields = [],
  genderPhiOptions = [],
  touchpointSchedules = [],
}) => {
  // Assessment definitions are loaded/cached once per module (shared with ProfileRowEditor's
  // own use of the same cache) — see ClientReportAssessments.ts.
  const [assessmentsBySourceId, setAssessmentsBySourceId] = useState<Map<string, AdminAssessmentDto>>(
    getCachedAssessmentsBySourceId() ?? new Map()
  );

  useEffect(() => {
    let cancelled = false;
    let refreshTimer: ReturnType<typeof setTimeout> | null = null;

    const syncFromCache = () => {
      void loadAssessmentsBySourceId().then((map) => {
        if (!cancelled) {
          setAssessmentsBySourceId(map);
        }
      });
    };

    syncFromCache();

    // Re-sync when the assessment-definition list is (re)written after mount — cold-start bundle
    // unpack, a debug manual download, an admin-result save. See ProfileRowEditor for the same
    // pattern; the module cache is invalidated by its own listener (registered first, at import).
    const handleContentUpdated = () => {
      if (refreshTimer) {
        clearTimeout(refreshTimer);
      }
      refreshTimer = setTimeout(syncFromCache, 250);
    };
    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
      window.addEventListener(ASSESSMENT_CONTENT_UPDATED_EVENT, handleContentUpdated);
    }

    return () => {
      cancelled = true;
      if (refreshTimer) {
        clearTimeout(refreshTimer);
      }
      if (typeof window !== 'undefined' && typeof window.removeEventListener === 'function') {
        window.removeEventListener(ASSESSMENT_CONTENT_UPDATED_EVENT, handleContentUpdated);
      }
    };
  }, []);

  const refreshBar = (
    <View style={styles.refreshBar}>
      <TouchableOpacity
        onPress={onRefresh}
        disabled={isLoading}
        accessibilityRole="button"
        accessibilityLabel="Refresh recent reports"
      >
        <Text style={[styles.refreshLinkText, isLoading && styles.refreshLinkTextDisabled]}>Refresh</Text>
      </TouchableOpacity>
    </View>
  );

  if (isLoading) {
    return (
      <View style={styles.tableWrap}>
        <View style={styles.centeredState}>
          <ActivityIndicator size="large" color={EDITOR_COLORS.accent} />
          <Text style={styles.loadingText}>Loading recent reports…</Text>
        </View>
        {refreshBar}
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.tableWrap}>
        <View style={styles.errorBox}>
          <Text style={styles.errorTitle}>Load failed</Text>
          <Text style={styles.errorMsg}>{error}</Text>
        </View>
        {refreshBar}
      </View>
    );
  }

  if (results.length === 0) {
    return (
      <View style={styles.tableWrap}>
        <View style={styles.centeredState}>
          <Text style={styles.emptyText}>No recent reports found.</Text>
        </View>
        {refreshBar}
      </View>
    );
  }

  const pageNum = parseInt(blocknum, 10);

  return (
    <View style={styles.tableWrap}>
      <View style={styles.list}>
        <View style={styles.headerRow}>
          <View style={styles.toggleCol}>
            <Text style={styles.headerText}></Text>
          </View>
          <View style={styles.firstNameCol}>
            <Text style={styles.headerText}>First Name</Text>
          </View>
          <View style={styles.lastNameCol}>
            <Text style={styles.headerText}>Last Name</Text>
          </View>
          {enableFlags.enable_idmgmt ? (
            <View style={styles.idCol}>
              <Text style={styles.headerText}>ID Number</Text>
            </View>
          ) : null}
          <View style={enableFlags.enable_idmgmt ? styles.nextCol : styles.nextColNoId}>
            <Text style={styles.headerText}>Next</Text>
          </View>
          <View style={enableFlags.enable_idmgmt ? styles.lastReportCol : styles.lastReportColNoId}>
            <Text style={styles.headerText}>Test Date</Text>
          </View>
          <View style={enableFlags.enable_idmgmt ? styles.viewReportCol : styles.viewReportColNoId}>
            <Text style={styles.headerText}>View Report</Text>
          </View>
        </View>

        {results.map((result, index) => {
          const profile = profiles.find((p) => p.patcode === result.patcode);
          const historyItem = profile?.history?.find((h) => h.Admin?.RecId === result.recid);

          if (!profile || !historyItem) {
            return null;
          }

          return (
            <ProfileRowEditor
              key={`${result.patcode}-${result.recid}-${index}`}
              profile={profile}
              onSave={onSaveProfile}
              onSendNow={onSendNow}
              onCancelLaunch={onCancelLaunch}
              onViewReport={onViewReport}
              enableFlags={enableFlags}
              summaryReportOverride={{
                testDate: historyItem.Admin.RecUTM,
                onViewReport: (mode: 'pdf' | 'inline') => {
                  const { assessmentDefinition, reports } = resolveHistoryItemReport(historyItem, assessmentsBySourceId);
                  // Same "Default Report" fallback ProfileRowEditor.tsx's own resolveSelectedReport
                  // uses, before falling back to plain reports[0] — see resolveDefaultReportId.
                  const defaultReportId = resolveDefaultReportId(assessmentDefinition, assessmentOptions);
                  const selectedReport = reports.find((r) => r.reportId === defaultReportId) ?? reports[0] ?? null;
                  onViewReport?.({
                    profile,
                    adminResponse: normalizeHistoryAdminResponse(historyItem.Admin),
                    assessmentDefinition,
                    selectedReport,
                    mode,
                  });
                },
              }}
              sendAssessmentOptions={sendAssessmentOptions}
              officeOnlineSraMembers={officeOnlineSraMembers}
              assessmentOptions={assessmentOptions}
              clientPhiFields={clientPhiFields}
              genderPhiOptions={genderPhiOptions}
              touchpointSchedules={touchpointSchedules}
            />
          );
        })}
      </View>

      {refreshBar}

      <View style={styles.pagination}>
        <TouchableOpacity
          onPress={onPrevPage}
          disabled={pageNum === 0}
          style={[styles.pageBtn, pageNum === 0 && styles.pageBtnDisabled]}
        >
          <Text style={[styles.pageBtnText, pageNum === 0 && styles.pageBtnTextDisabled]}>← Prev</Text>
        </TouchableOpacity>

        <Text style={styles.pageNum}>Page {pageNum + 1}</Text>

        <TouchableOpacity
          onPress={onNextPage}
          disabled={!hasMore}
          style={[styles.pageBtn, !hasMore && styles.pageBtnDisabled]}
        >
          <Text style={[styles.pageBtnText, !hasMore && styles.pageBtnTextDisabled]}>Next →</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

// Column widths/labels intentionally mirror ManageClientsResultsList's — this list renders
// the same ProfileRowEditor summary row underneath (see summaryReportOverride).
const styles = StyleSheet.create({
  tableWrap: {
    width: '100%',
    maxWidth: 850,
    alignSelf: 'center',
  },
  refreshBar: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    paddingTop: 8,
  },
  refreshLinkText: {
    ...editorText,
    color: EDITOR_COLORS.accent,
  },
  refreshLinkTextDisabled: {
    color: EDITOR_COLORS.placeholder,
  },
  centeredState: {
    paddingVertical: 40,
    alignItems: 'center',
    gap: 10,
  },
  loadingText: {
    ...editorText,
    color: EDITOR_COLORS.textMuted,
  },
  emptyText: {
    ...editorText,
    color: EDITOR_COLORS.textMuted,
    textAlign: 'center',
    maxWidth: 400,
    lineHeight: 20,
  },
  errorBox: {
    backgroundColor: EDITOR_COLORS.errorBackground,
    borderRadius: 12,
    padding: 16,
  },
  errorTitle: {
    ...editorText,
    color: EDITOR_COLORS.errorText,
    marginBottom: 4,
  },
  errorMsg: {
    ...editorText,
    color: EDITOR_COLORS.errorText,
    lineHeight: 19,
  },
  list: {
    width: '100%',
    borderBottomWidth: 1,
    borderBottomColor: EDITOR_COLORS.separator,
  },
  headerRow: {
    minHeight: 40,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: EDITOR_COLORS.background,
    borderBottomWidth: 1,
    borderBottomColor: EDITOR_COLORS.separator,
  },
  headerText: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
  },
  toggleCol: {
    width: '8%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  firstNameCol: {
    width: '16%',
    paddingHorizontal: 6,
  },
  lastNameCol: {
    width: '16%',
    paddingHorizontal: 6,
  },
  idCol: {
    width: '15%',
    paddingHorizontal: 6,
  },
  nextCol: {
    width: '14%',
    paddingHorizontal: 6,
  },
  nextColNoId: {
    width: '20%',
    paddingHorizontal: 6,
  },
  lastReportCol: {
    width: '16%',
    paddingHorizontal: 6,
  },
  lastReportColNoId: {
    width: '24%',
    paddingHorizontal: 6,
  },
  viewReportCol: {
    width: '15%',
    paddingHorizontal: 6,
    alignItems: 'center',
  },
  viewReportColNoId: {
    width: '16%',
    paddingHorizontal: 6,
    alignItems: 'center',
  },
  pagination: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    paddingTop: 20,
  },
  pageBtn: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: EDITOR_COLORS.submitBackground,
  },
  pageBtnDisabled: {
    opacity: 0.4,
  },
  pageBtnText: {
    ...editorText,
    color: EDITOR_COLORS.submitText,
  },
  pageBtnTextDisabled: {
    color: EDITOR_COLORS.placeholder,
  },
  pageNum: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
  },
});
